import { UNISWAP_SUBGRAPH_PATH } from './constants';
import {
  beachContract,
  surfTokenBscContract,
  surfTokenXDaiContract,
  titoContract,
  towelTokenContract,
  whirlpoolContract,
} from './contracts';
import { parseEther } from './price';
import { ChainId, ContractAddress, GlobalStats, Pair, Token } from './types';

const getEthUniswapStats = async (): Promise<Partial<GlobalStats>> => {
  const query = `{
    pair(id: "${Pair.ETH_SURF}") {
      reserve0,
      reserve1
    }
  }`;
  const response = await fetch(UNISWAP_SUBGRAPH_PATH, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
  const { data } = await response.json();
  const { reserve0: otherReserve, reserve1: tokenReserve } = data.pair;
  return {
    tokenReserve: parseFloat(tokenReserve),
    otherReserve: parseFloat(otherReserve),
  };
};

const getEthStats = async (): Promise<Partial<GlobalStats>> => {
  const [
    { info: surfInfo },
    { info: titoInfo },
    {
      metadatas: [metadata],
    },
    uniswapStats,
  ] = await Promise.all([
    titoContract.getAllInfoFor(Token.ETH_SURF),
    whirlpoolContract.getAllInfoFor(ContractAddress.Tito),
    titoContract.getAllPoolInfoFor(ContractAddress.Surf3d),
    getEthUniswapStats(),
  ]);
  const totalSupply = parseEther(surfInfo[3]);
  const lpTokensLocked = parseEther(surfInfo[5]);
  const totalBurnt = parseEther(surfInfo[7]);
  const whirlpoolBalance = parseEther(titoInfo[0]);
  const whirlpoolTotalStaked = parseEther(titoInfo[5]);
  const titoBalance = parseEther(titoInfo[6]);
  const lpTokensTotal = parseEther(metadata[1]);
  const lpPrice = parseEther(metadata[4]);
  const s3dBalance = parseEther(metadata[8]);
  const { tokenReserve = 0 } = uniswapStats;
  return {
    ...uniswapStats,
    totalSupply,
    totalBurnt,
    totalLocked:
      titoBalance +
      whirlpoolBalance +
      s3dBalance +
      totalBurnt +
      (tokenReserve * (lpTokensLocked + whirlpoolTotalStaked)) / lpTokensTotal,
    lpPrice,
    lpTokensLocked,
    lpTokensTotal,
    whirlpoolBalance,
    whirlpoolTotalStaked,
    towelTotalSupply: 0,
    towelTotalStaked: 0,
  };
};

const getBscStats = async (): Promise<Partial<GlobalStats>> => {
  const totalSupply = await surfTokenBscContract.totalSupply();
  return {
    totalSupply: parseEther(totalSupply),
  };
};

const getXDaiStats = async (): Promise<Partial<GlobalStats>> => {
  const totalSupply = await surfTokenXDaiContract.totalSupply();
  return {
    totalSupply: parseEther(totalSupply),
  };
};

const getMaticStats = async (): Promise<Partial<GlobalStats>> => {
  const [quickSwapInfo, towelStakeInfo] = await Promise.all([
    beachContract.surfQuickswapInfoFor(Token.TOWEL),
    towelTokenContract.allInfoFor(ContractAddress.TowelStake),
  ]);
  const totalSupply = parseEther(quickSwapInfo.totalTokens);
  const lpTokensTotal = parseEther(quickSwapInfo.totalLPTokens);
  const tokenReserve = parseEther(quickSwapInfo.tokenReserve);
  const otherReserve = parseEther(quickSwapInfo.otherReserve);
  return {
    totalSupply,
    totalLocked: tokenReserve + parseEther(quickSwapInfo.userBalance),
    lpPrice: (2 * otherReserve) / lpTokensTotal,
    lpTokensLocked: lpTokensTotal,
    lpTokensTotal,
    tokenReserve,
    otherReserve,
    whirlpoolBalance: 0,
    whirlpoolTotalStaked: 0,
    towelTotalSupply: parseEther(towelStakeInfo.totalTokens),
    towelTotalStaked: parseEther(towelStakeInfo.userBalance),
  };
};

export const getAllGlobalStats = async (): Promise<Record<ChainId, Partial<GlobalStats>>> => {
  const [ethStats, bscStats, xDaiStats, maticStats] = await Promise.all([
    getEthStats(),
    getBscStats(),
    getXDaiStats(),
    getMaticStats(),
  ]);
  return {
    [ChainId.EthMainnet]: ethStats,
    [ChainId.BscMainnet]: bscStats,
    [ChainId.XDai]: xDaiStats,
    [ChainId.MaticMainnet]: maticStats,
  };
};
